import { keyframes } from '@emotion/react';
import DeleteIcon from '@mui/icons-material/Cancel';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Tooltip } from '@mui/material';
import useColorsStyles from 'common/colors';
import useFlexBoxStyles from 'common/flexBox';
import { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => {
    const fadeIn = keyframes`
        0% {
            width: 0;
        }
        100% {
            width: 100%;
        }
    `;

    const fadeOut = keyframes`
        0% {
            width: 100%;
        }
        100% {
            width: 0;
            display: none;
        }
    `;

    const bellAnimation = keyframes`
        0%,
        100% {
            transform: rotate(0deg);
            transform-origin: 50% 50%;
        }
    
        10% {
            transform: rotate(8deg);
        }
    
        20%,
        40%,
        60% {
            transform: rotate(-10deg);
        }
    
        30%,
        50%,
        70% {
            transform: rotate(10deg);
        }
    
        80% {
            transform: rotate(-8deg);
        }
    
        90% {
            transform: rotate(8deg);
        }`;

    return {
        root: {
            borderRadius: theme.spacing(3),
            cursor: 'default',
        },
        label: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            '&.over': {
                animation: `${fadeIn} 0.3s`,
            },
            '&:not(.over)': {
                animation: `${fadeOut} 0.3s`,
            },
        },
        action: {
            width: theme.spacing(2),
            height: theme.spacing(2),
            top: theme.spacing(-0.5),
            right: theme.spacing(-0.5),
            color: theme.palette.common.gray,
            background: theme.palette.common.white,
        },
        bell: {
            animation: `${bellAnimation} 1s ease 0s infinite normal forwards`,
        },
    };
});

const ChannelsChipOver = ({
    avatar,
    className,
    onDelete,
    label,
    onAdd,
    tooltipLabel,
    tooltip,
    showLabel,
    children,
    selected,
    disabled,
}) => {
    const { classes, cx } = useStyles();
    const { classes: flexBox } = useFlexBoxStyles();
    const { classes: colors } = useColorsStyles();
    const [over, setOver] = useState(false);

    const isOver = over || showLabel;

    const root = (
        <div
            className={cx(
                classes.root,
                flexBox.flex,
                flexBox.alignItemsCenter,
                isOver && flexBox.gap1,
                flexBox.relative,
                Boolean(onAdd) && !disabled && flexBox.cursorPointer,
                className,
            )}
            aria-hidden="true"
            onMouseEnter={() => setOver(true)}
            onMouseLeave={() => setOver(false)}
            onClick={() => {
                if (onAdd && !disabled) {
                    onAdd();
                }
            }}
        >
            {avatar}
            {label && (showLabel || !tooltipLabel) ? (
                <span className={cx(classes.label, isOver && 'over')} style={{ width: isOver ? '100%' : 0 }}>
                    {label}
                </span>
            ) : null}
            {onDelete && over && !disabled ? (
                <DeleteIcon
                    className={cx(classes.action, flexBox.absolute, flexBox.cursorPointer, flexBox.borderRadius50)}
                    onClick={onDelete}
                />
            ) : null}

            {selected && (
                <NotificationsIcon
                    className={cx(
                        classes.action,
                        classes.bell,
                        flexBox.absolute,
                        flexBox.cursorPointer,
                        flexBox.borderRadius50,
                        colors.textPrimaryMain,
                    )}
                />
            )}

            {children}
        </div>
    );

    if (tooltipLabel || tooltip) {
        return <Tooltip title={tooltip || label}>{root}</Tooltip>;
    }

    return root;
};

export default ChannelsChipOver;
